import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";

import Overlay from "../../../Componentes/Overlay";
import FormComp from "../../../Componentes/FormComp";
import formStyle from "../../../Componentes/FormComp/FormComp.module.css";
import Modal from "../../../Componentes/Utils/Modal";
import Formulario from "../../../Componentes/Utils/Formulario";
import MultiFileUploader from "../../../Componentes/Utils/MultiFileUploader";
import { formatCPF } from "../../../Funcoes/FormatCPF";
import { agoraDatetimeLocal, RESULTADO_TESTE_OPTIONS, TIPO_TESTE_OPTIONS } from "./utils";
import { useToast } from "../../../contexts/ToastContext";
import useApi from "../../../hooks/useApi";

const ModalTitle = styled.h3`
    margin: 0 0 18px 0;
    padding: 0 0 12px 2px;
    border-bottom: 1px solid #e5e7eb;
    font-size: 15px;
    font-weight: 700;
    color: #263b4b;
    letter-spacing: 0.02em;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
`;

const FormSection = styled.div`
    width: 100%;
    text-align: left;

    .${formStyle.formChild} p {
        margin: 15px 0 5px 0;
        padding-left: 2px;
        text-align: left;
    }
`;

const BotaoSalvar = styled.button`
    margin-top: 18px;
    width: 100%;
    height: 42px;
    border: none;
    border-radius: 10px;
    background-color: #4caf50;
    color: #fff;
    font-weight: bold;
    font-size: 13px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) { background-color: #45a049; }
    &:disabled { background-color: #c1c1c1; cursor: not-allowed; }
`;

const emptyColab = {
    nome: "",
    matricula: "",
    funcao: "",
    centro_custo: "",
    contrato: "",
    lider: "",
};

const ModalNovoTeste = ({ fechar, onSaved }) => {
    const api = useApi();
    const { notifyError, notifySuccess } = useToast();

    const [cpf, setCpf] = useState("");
    const [colab, setColab] = useState(emptyColab);
    const [tipoTeste, setTipoTeste] = useState("");
    const [resultado, setResultado] = useState("");
    const [observacoes, setObservacoes] = useState("");
    const [dataHora, setDataHora] = useState(() => agoraDatetimeLocal());
    const [anexos, setAnexos] = useState([]);
    const [saving, setSaving] = useState(false);

    const formRef = useRef(null);
    const requestIdRef = useRef(0);

    const clearColab = useCallback(() => setColab(emptyColab), []);

    useEffect(() => {
        const digits = cpf.replace(/\D/g, "");
        if (digits.length !== 11) {
            clearColab();
            return undefined;
        }

        const currentRequest = ++requestIdRef.current;

        const fetchColab = async () => {
            try {
                const { data } = await api.get("/Painel/API/AtendimentoAmb/ColabByCpf/index.php", {
                    params: { cpf: digits },
                });

                if (currentRequest !== requestIdRef.current) return;

                if (!data?.success) {
                    clearColab();
                    notifyError(data?.message || "Colaborador não encontrado.");
                    return;
                }

                setColab({
                    nome: data.nome ?? "",
                    matricula: data.matricula ?? "",
                    funcao: data.funcao ?? "",
                    centro_custo: data.centro_custo ?? "",
                    contrato: data.contrato ?? "",
                    lider: data.lider ?? "",
                });
            } catch (err) {
                if (currentRequest !== requestIdRef.current) return;
                clearColab();
                notifyError(err?.response?.data?.message || "Erro ao buscar colaborador.");
            }
        };

        fetchColab();
        return () => { requestIdRef.current += 1; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cpf, clearColab]);

    const handleCpfChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 11);
        setCpf(digits);
    };

    const validateForm = useCallback(() => {
        const form = formRef.current;
        if (form && !form.checkValidity()) {
            form.reportValidity();
            return false;
        }
        if (!colab.nome) {
            notifyError("Informe um CPF válido para carregar o colaborador.");
            return false;
        }
        if (!tipoTeste) {
            notifyError("Selecione o tipo de teste.");
            return false;
        }
        if (!resultado) {
            notifyError("Selecione o resultado do teste.");
            return false;
        }
        return true;
    }, [colab.nome, tipoTeste, resultado, notifyError]);

    const handleSalvar = async (e) => {
        e.preventDefault();
        if (saving) return;
        if (!validateForm()) return;

        setSaving(true);

        const formData = new FormData();
        formData.append("cpf", cpf);
        formData.append("nome", colab.nome);
        formData.append("matricula", colab.matricula ?? "");
        formData.append("funcao", colab.funcao ?? "");
        formData.append("centro_custo", colab.centro_custo ?? "");
        formData.append("contrato", colab.contrato ?? "");
        formData.append("lider", colab.lider ?? "");
        formData.append("tipo_teste", tipoTeste);
        formData.append("resultado", resultado);
        formData.append("observacoes", observacoes);
        formData.append("data_hora", dataHora);

        anexos.forEach((item) => {
            if (item?.file) formData.append("files[]", item.file);
        });

        try {
            const { data } = await api.post(
                "/Painel/API/AtendimentoAmb/TestesAlcoolDrogas/Save/index.php",
                formData
            );

            if (data?.success) {
                notifySuccess(data?.message || "Teste salvo.");
                onSaved?.();
                fechar?.();
                return;
            }
            notifyError(data?.message || "Erro ao salvar teste.");
        } catch (err) {
            console.error(err);
            notifyError(err?.response?.data?.message || "Erro ao salvar teste.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Overlay state>
            <Modal header close={fechar} size="md">
                <ModalTitle>NOVO TESTE</ModalTitle>

                <FormSection>
                    <Formulario ref={formRef} onSubmit={handleSalvar}>
                        <FormComp
                            title="CPF"
                            type="text"
                            placeholder="000.000.000-00"
                            valor={formatCPF(cpf)}
                            changeFunc={handleCpfChange}
                            required
                        />
                        <FormComp title="NOME" type="text" valor={colab.nome} readOnly />
                        <FormComp title="FUNÇÃO" type="text" valor={colab.funcao} readOnly />
                        <FormComp title="CENTRO DE CUSTO" type="text" valor={colab.centro_custo} readOnly />
                        <FormComp title="CONTRATO" type="text" valor={colab.contrato} readOnly />
                        <FormComp title="LÍDER" type="text" valor={colab.lider} readOnly />
                        <FormComp
                            title="TIPO DE TESTE"
                            type="select"
                            obj
                            options={TIPO_TESTE_OPTIONS}
                            valor={tipoTeste}
                            changeFunc={(e) => setTipoTeste(e.target.value)}
                            required
                        />
                        <FormComp
                            title="RESULTADO"
                            type="select"
                            obj
                            options={RESULTADO_TESTE_OPTIONS}
                            valor={resultado}
                            changeFunc={(e) => setResultado(e.target.value)}
                            required
                        />
                        <FormComp
                            title="OBSERVAÇÕES"
                            type="textarea"
                            valor={observacoes}
                            changeFunc={(e) => setObservacoes(e.target.value)}
                        />
                        <FormComp
                            title="DATA / HORA"
                            type="datetime-local"
                            valor={dataHora}
                            changeFunc={(e) => setDataHora(e.target.value)}
                            required
                        />

                        <MultiFileUploader files={anexos} setFiles={setAnexos} />

                        <BotaoSalvar type="submit" disabled={saving}>
                            {saving ? "SALVANDO…" : "SALVAR TESTE"}
                        </BotaoSalvar>
                    </Formulario>
                </FormSection>
            </Modal>
        </Overlay>
    );
};

export default ModalNovoTeste;
